import AppDialog from '../../../../../components/common/AppDialog';
import AppButton from '../../../../../components/common/AppButton';
import {useSnackbar} from '../../../../../context/SnackbarContextProvider';
import {SVG} from '../../../../../constants/assetLists';
import {z} from 'zod';
import {Controller, FormProvider, useForm} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';
import AppTextInput from '../../../../../components/common/AppTextInput';
import AppMUIAutocomplete from '../../../../../components/common/AppMUIAutoComplete';
import AppSwitch from '../../../../../components/common/AppSwitch';
import {useEffect, useMemo, useState} from 'react';
import {COLORS, ICONS} from '../../../data';
import {useGroupsContext} from '../../../../../context/users/GroupsContextProvider';
import UsersList from './UsersList';
import {useMutation, useQuery} from '@tanstack/react-query';
import {
  handleCreateUserGroupService,
  handleGetUserGroupService,
  handleUpdateUserGroupService,
} from '../../../../../services/userGroups';
import {ApiResponse} from '../../../../../services/axiosInstance';
import QUERY_KEYS from '../../../../../constants/queryKeys';
import {SingleGroupResponse} from '../groups';
import {useClientsContext} from '../../../../../context/clients/ClientsContextProvider';
import {useDebounce} from '../../../../../hooks/useDebunce';
import {UsersListResponse} from '../../UserScreens/users';
import {handleGetAllUsersService} from '../../../../../services/users';

const zodSchema = z.object({
  isActive: z.boolean(),
  groupName: z.string({error: 'שדה חובה!'}).nonempty('שדה חובה!'),
  description: z.string().optional(),
  clientId: z.string({error: 'שדה חובה!'}).nonempty('שדה חובה!'),
  branchId: z.string().optional(),
  icon: z.string({error: 'שדה חובה!'}).nonempty('שדה חובה!'),
  color: z.string({error: 'שדה חובה!'}).nonempty('שדה חובה!'),
  associatedUsers: z
    .array(z.string('שדה חובה!').nonempty('שדה חובה!'), {error: 'שדה חובה!'})
    .min(1, 'שדה חובה!'),
});

export type UsersGroupFormValues = z.infer<typeof zodSchema>;

function AddEditGroupDialog() {
  const {showSnackbar} = useSnackbar();
  const {selectedLocation, allClients} = useClientsContext();

  const {
    showAddEditDialog,
    setShowAddEditDialog,
    selectedGroup,
    setSelectedGroup,
    refetchAllUserGroups,
  } = useGroupsContext();

  const [searchText, setSearchText] = useState('');

  const debouncedSearch = useDebounce(searchText, 500);

  const method = useForm<UsersGroupFormValues>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: {
      isActive: true,
      groupName: '',
      description: '',
      clientId: '',
      branchId: '',
      icon: '',
      color: '',
      associatedUsers: [],
    },
    resolver: zodResolver(zodSchema),
  });

  const {
    formState: {errors, isSubmitting},
    reset,
    control,
    setValue,
    handleSubmit,
    watch,
  } = method;

  const clientId = watch('clientId');
  const users = watch('associatedUsers');
  const branchId = watch('branchId');

  const clientBranchesData = useMemo(() => {
    return allClients?.find(client => client.id === clientId)?.branches ?? [];
  }, [allClients, clientId]);

  const {data: userGroupData} = useQuery<
    ApiResponse<SingleGroupResponse>,
    Error,
    SingleGroupResponse
  >({
    queryKey: [QUERY_KEYS.GET_SINGLE_USER_GROUP, selectedGroup],
    queryFn: () => handleGetUserGroupService(selectedGroup),
    enabled: !!selectedGroup && !!showAddEditDialog,
    staleTime: 0,
  });

  const {data: allUsers} = useQuery<
    ApiResponse<UsersListResponse>,
    Error,
    UsersListResponse
  >({
    queryKey: [QUERY_KEYS.GET_ALL_USERS, clientId, branchId, debouncedSearch],
    queryFn: () =>
      handleGetAllUsersService({
        search: debouncedSearch,
        clientId: clientId,
        ...(branchId && {branchId: branchId}),
        status: 'active',
      }),
    enabled: !!clientId || !!branchId,
  });

  useEffect(() => {
    if (selectedLocation && showAddEditDialog) {
      if (selectedLocation?.isClient) {
        setValue('clientId', selectedLocation?.id);
      } else {
        setValue('clientId', selectedLocation?.clientId);
        setValue('branchId', selectedLocation?.id);
      }
    }
  }, [selectedLocation, showAddEditDialog]);

  useEffect(() => {
    if (showAddEditDialog && selectedGroup && userGroupData?.data) {
      const group = userGroupData?.data;

      setValue('isActive', group?.isActive);
      setValue('groupName', group?.groupName || '');
      setValue('description', group?.description || '');
      setValue('clientId', group?.clientId?._id || '');
      setValue('branchId', group?.branchId?._id || '');
      setValue('icon', group?.icon || '');
      setValue('color', group?.color || '');
      setValue(
        'associatedUsers',
        group?.associatedUsers?.map((user: any) => user?._id) || [],
      );
    }
  }, [showAddEditDialog, selectedGroup, userGroupData]);

  const handleModalClose = () => {
    reset();
    setSelectedGroup(null);
    setShowAddEditDialog(false);
  };

  const onSubmit = (formData: UsersGroupFormValues) => {
    const {branchId, ...rest} = formData;
    const payload = {
      ...rest,
      ...(formData.branchId && {branchId: formData.branchId}),
    };

    if (selectedGroup) {
      handleUpdateUserGroup({id: selectedGroup, payload});
    } else {
      handleCreateUserGroup(payload);
    }
  };

  const {mutate: handleCreateUserGroup, isPending: isAdding} = useMutation({
    mutationFn: handleCreateUserGroupService,
    onSuccess: data => {
      showSnackbar({
        message: data?.message ?? 'רשימת פתיחת בר נוצרה',
        type: 'success',
      });
      handleModalClose();
      refetchAllUserGroups();
    },
  });

  const {mutate: handleUpdateUserGroup, isPending: isUpdating} = useMutation({
    mutationFn: ({id, payload}: {id: string; payload: any}) =>
      handleUpdateUserGroupService(id, payload),
    onSuccess: data => {
      showSnackbar({
        message: data?.message ?? 'רשימת פתיחת בר נוצרה',
        type: 'success',
      });
      handleModalClose();
      refetchAllUserGroups();
    },
    onError: errors => {
      showSnackbar({
        message: errors?.message || 'הקבוצה לא עודכנה, נסו שנית',
        type: 'error',
      });
    },
  });

  return (
    <AppDialog
      showModel={showAddEditDialog}
      title={selectedGroup ? 'עריכת קבוצה' : 'הוספת קבוצה'}
      titleIcon={
        <img
          src={selectedGroup ? SVG.outlinePencilDarkIcon : SVG.addGrayIcon}
          alt="icon"
          className="w-[14px] h-[14px] object-contain"
        />
      }
      onClose={handleModalClose}
      showHeaderLine={false}
      width={717}
      fullHeight>
      <div className="h-full flex flex-col gap-4 pt-4">
        <div className="flex-1 min-h-0 flex flex-col gap-4 px-[40px] overflow-y-auto transition-all duration-300">
          <div className="flex flex-col gap-3">
            {selectedGroup && (
              <div className="flex justify-end">
                <div className="flex items-center gap-2">
                  <Controller
                    name="isActive"
                    control={control}
                    render={({field: {onChange, value}}) => (
                      <AppSwitch onChange={onChange} checked={value} />
                    )}
                  />
                  <p
                    className={`m-0 ${watch('isActive') ? 'font-medium' : ''}`}>
                    תפקיד פעיל
                  </p>
                </div>
              </div>
            )}

            <Controller
              name="groupName"
              control={control}
              render={({field}) => {
                return (
                  <div className="flex items-center">
                    <div className="w-10">
                      <img
                        src={SVG.peopleIcon}
                        alt="icon"
                        className="w-[24px] h-[24px] object-contain"
                      />
                    </div>

                    <AppTextInput
                      type="text"
                      label="שם קבוצה"
                      placeholder="שם קבוצה"
                      className="border-0"
                      {...field}
                      error={errors.groupName?.message || ''}
                    />
                  </div>
                );
              }}
            />

            <Controller
              name="description"
              control={control}
              render={({field}) => {
                return (
                  <div className="flex items-center">
                    <div className="w-10" />

                    <AppTextInput
                      multiline
                      label="תיאור"
                      placeholder="תיאור"
                      className="border-0"
                      {...field}
                      error={errors.description?.message || ''}
                    />
                  </div>
                );
              }}
            />

            <Controller
              name="clientId"
              control={control}
              render={({field: {value}}) => {
                const selectedClient = selectedLocation
                  ? selectedLocation?.isClient
                    ? selectedLocation?.id
                    : selectedLocation?.clientId
                  : null;
                const clientsData = allClients?.filter(
                  client => client.id === selectedClient,
                );

                return (
                  <div className="flex items-center">
                    <div className="w-10">
                      <img
                        src={SVG.clientIcon}
                        className="w-[20px] h-[18px] object-contain"
                      />
                    </div>

                    <AppMUIAutocomplete
                      label="מקומות"
                      size="small"
                      required
                      fullWidth
                      options={clientsData || []}
                      getOptionLabel={option => option?.name}
                      value={
                        clientsData?.find((item: any) => item?.id === value) ||
                        null
                      }
                      onChange={(_e, val) => {
                        if (val) {
                          setValue('clientId', val?.id, {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                        } else {
                          setValue('clientId', '', {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                        }
                        setValue('branchId', '', {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                        setValue('associatedUsers', [], {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                      }}
                      error={!!errors.clientId}
                      helperText={errors.clientId?.message}
                    />
                  </div>
                );
              }}
            />

            <Controller
              name="branchId"
              control={control}
              render={({field: {value}}) => {
                return (
                  <div className="flex items-center">
                    <div className="w-10">
                      <img
                        src={SVG.filterSidebarRestaurantIcon}
                        className="w-[20px] h-[18px] object-contain"
                      />
                    </div>

                    <AppMUIAutocomplete
                      label="סניף"
                      size="small"
                      fullWidth
                      options={clientBranchesData || []}
                      getOptionLabel={option => option?.name}
                      value={
                        clientBranchesData?.find(
                          (item: any) => item?.id === value,
                        ) || null
                      }
                      onChange={(_e, val) => {
                        if (val) {
                          setValue('branchId', val?.id, {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                        } else {
                          setValue('branchId', '', {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                        }
                        setValue('associatedUsers', [], {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                      }}
                      error={!!errors.clientId}
                      helperText={errors.clientId?.message}
                    />
                  </div>
                );
              }}
            />

            <div className="flex items-start gap-3">
              <div className="w-10" />

              <Controller
                name="icon"
                control={control}
                render={({field: {value}}) => {
                  return (
                    <AppMUIAutocomplete
                      label="איקון"
                      size="small"
                      options={ICONS}
                      fullWidth
                      getOptionLabel={o => o.id}
                      slotProps={{
                        listbox: {
                          sx: {
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            gap: 1,
                            p: 1,
                            maxHeight: 320,
                            overflowY: 'auto',
                            '& .MuiAutocomplete-option': {
                              p: '8px',
                            },
                          },
                        },
                      }}
                      renderValue={value =>
                        value && (
                          <div className="flex items-center gap-1">
                            <img
                              src={value?.icon}
                              alt="icon"
                              className="w-[20px] h-[20px] object-contain"
                            />
                            <p>איקון</p>
                          </div>
                        )
                      }
                      renderOption={(props, option) => {
                        const {key, ...optionProps} = props;

                        return (
                          <li key={key} {...optionProps}>
                            <img
                              src={option?.icon}
                              alt="icon"
                              className="w-[32px] h-[32px] object-contain"
                            />
                          </li>
                        );
                      }}
                      required
                      value={ICONS.find(item => item?.id === value) || null}
                      onChange={(_e, val) => {
                        if (val) {
                          setValue('icon', val?.id, {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                        } else {
                          setValue('icon', '', {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                        }
                      }}
                      error={!!errors.icon}
                      helperText={errors.icon?.message}
                    />
                  );
                }}
              />

              <Controller
                name="color"
                control={control}
                render={({field: {value}}) => {
                  return (
                    <AppMUIAutocomplete
                      label="צבע"
                      size="small"
                      options={COLORS}
                      fullWidth
                      getOptionLabel={o => o?.color} // avoid empty label space
                      slotProps={{
                        listbox: {
                          sx: {
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            gap: 1,
                            p: 1,
                            maxHeight: 320,
                            overflowY: 'auto',
                            '& .MuiAutocomplete-option': {
                              p: '8px',
                              '&.Mui-focused, &[aria-selected="true"]': {
                                backgroundColor: 'transparent !important',
                              },
                            },
                          },
                        },
                      }}
                      renderValue={value =>
                        value && (
                          <div
                            className="w-[24px] h-[24px] rounded-[3px] border-1 border-[#777777]"
                            style={{backgroundColor: value?.color}}
                          />
                        )
                      }
                      renderOption={(props, option) => {
                        const {key, ...optionProps} = props;
                        const isSelected = value === option?.color;

                        return (
                          <li key={key} {...optionProps}>
                            <div
                              className="w-[24px] h-[24px] rounded-[3px]"
                              style={{
                                backgroundColor: option?.color,
                                border: isSelected
                                  ? '2px solid #333333'
                                  : '1px solid #333333',
                              }}
                            />
                          </li>
                        );
                      }}
                      required
                      value={COLORS.find(item => item?.color === value) || null}
                      onChange={(_e, val) => {
                        if (val) {
                          setValue('color', val?.color, {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                        } else {
                          setValue('color', '', {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                        }
                      }}
                      error={!!errors.color}
                      helperText={errors.color?.message}
                    />
                  );
                }}
              />
            </div>
          </div>

          <FormProvider {...method}>
            <UsersList
              allUsers={allUsers}
              searchText={searchText}
              setSearchText={setSearchText}
              debouncedSearch={debouncedSearch}
              users={users}
              onUserSelection={ids => {
                setValue('associatedUsers', ids?.map(String) || [], {
                  shouldValidate: true,
                  shouldDirty: true,
                });
              }}
              error={errors.associatedUsers?.message}
            />
          </FormProvider>
        </div>

        <div className="flex justify-end px-[48px]">
          <div className="flex items-center gap-2">
            <AppButton onClick={handleModalClose}>ביטול</AppButton>

            <AppButton
              variant="primary"
              isLoading={isSubmitting || isAdding || isUpdating}
              onClick={handleSubmit(onSubmit)}>
              שמירה
            </AppButton>
          </div>
        </div>
      </div>
    </AppDialog>
  );
}

export default AddEditGroupDialog;
